/**
 * Ce que les mains peuvent porter, et ce que ça coûte.
 *
 * Module volontairement pur : aucune dépendance à Three.js ni au DOM, pour que
 * l'équilibrage du portage se teste en une milliseconde plutôt qu'en dix
 * allers-retours entre l'appartement et le coffre.
 */

/**
 * Ce que la caméra montre au premier plan selon ce qu'on trimballe.
 * `crate` est le bac de périphériques, clavier compris; `box` est la caisse
 * générique de tout le reste — une glacière ne doit pas montrer un clavier.
 */
export type RigKind =
  | "none"
  | "tower"
  | "screen"
  | "crate"
  | "box"
  | "dolly"
  | "wheel";

export interface CargoSpec {
  id: string;
  /** Libellé du prompt d'interaction. */
  label: string;
  /** Version courte pour la pastille de charge du HUD. */
  short: string;
  /** Place occupée dans les bras, sur 3. */
  bulk: number;
  rig: RigKind;
  /** Sans ça, on ne joue pas : la voiture refuse de partir. */
  essential: boolean;
}

/** Deux bras, trois unités d'encombrement. Gisèle en vaut trois à elle seule. */
export const HANDS_CAPACITY = 3;

/** Le diable annule la majeure partie de la pénalité, pas sa totalité. */
const DOLLY_RELIEF = 0.62;

export function specOf(
  id: string,
  table: Readonly<Record<string, CargoSpec>>,
): CargoSpec | undefined {
  return table[id];
}

export function carriedBulk(
  ids: readonly string[],
  table: Readonly<Record<string, CargoSpec>>,
): number {
  return ids.reduce((total, id) => total + (table[id]?.bulk ?? 0), 0);
}

export function freeHands(
  ids: readonly string[],
  table: Readonly<Record<string, CargoSpec>>,
): number {
  return Math.max(0, HANDS_CAPACITY - carriedBulk(ids, table));
}

export function canCarry(
  ids: readonly string[],
  id: string,
  table: Readonly<Record<string, CargoSpec>>,
): boolean {
  const spec = table[id];
  if (!spec || ids.includes(id)) return false;
  return carriedBulk(ids, table) + spec.bulk <= HANDS_CAPACITY;
}

/**
 * Le rig affiche l'objet le plus encombrant : c'est lui qui masque la vue, donc
 * c'est lui que le joueur doit voir.
 */
export function rigForCarry(
  ids: readonly string[],
  table: Readonly<Record<string, CargoSpec>>,
  onDolly = false,
): RigKind {
  let best: CargoSpec | undefined;
  for (const id of ids) {
    const spec = table[id];
    if (spec && (!best || spec.bulk > best.bulk)) best = spec;
  }
  if (!best) return "none";
  return onDolly ? "dolly" : best.rig;
}

/** Multiplicateur de vitesse : 1 à vide, ~0.6 avec les bras pleins. */
export function carrySpeedFactor(bulk: number, onDolly = false): number {
  if (!Number.isFinite(bulk) || bulk <= 0) return 1;
  const penalty = 0.135 * (onDolly ? 1 - DOLLY_RELIEF : 1);
  return Math.max(0.45, 1 - Math.min(bulk, HANDS_CAPACITY) * penalty);
}

/** Énergie perdue par seconde de marche sous la charge. */
export function carryDrain(bulk: number, onDolly = false): number {
  if (!Number.isFinite(bulk) || bulk <= 0) return 0;
  return Math.min(bulk, HANDS_CAPACITY) * 0.34 * (onDolly ? 1 - DOLLY_RELIEF : 1);
}

/** Les bras pleins, on ne court pas. On avance en niant la douleur. */
export function canRunWith(bulk: number, onDolly = false): boolean {
  return onDolly || bulk < HANDS_CAPACITY;
}

/** Le balancement de tête grossit avec la charge : 18 kg, ça se voit. */
export function bobScale(bulk: number): number {
  if (!Number.isFinite(bulk) || bulk <= 0) return 1;
  return 1 + Math.min(bulk, HANDS_CAPACITY) * 0.38;
}

/** Les identifiants essentiels qui manquent encore dans le coffre. */
export function missingEssentials(
  loaded: readonly string[],
  table: Readonly<Record<string, CargoSpec>>,
): CargoSpec[] {
  return Object.values(table).filter(
    (spec) => spec.essential && !loaded.includes(spec.id),
  );
}
